import React, { useCallback, useEffect, useState } from "react";
import { CheckCircle2, CreditCard, Gauge, Loader2, RefreshCw, Users } from "lucide-react";
import { toast } from "sonner";
import AdminShell from "../../components/layout/AdminShell";
import { useAuth } from "../../context/AuthContext";
import { BillingApi, errorMessage } from "../../lib/api";
import { formatDateTime, formatMoney } from "../../lib/format";
import { planFeatures, planName, planTagline, seatLimitLabel, seatPercent } from "../../lib/plans";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Progress } from "../../components/ui/progress";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";
import { useI18n } from "../../i18n";

const Billing = () => {
  const { t } = useI18n();
  const { currency } = useAuth();
  const [config, setConfig] = useState(null);
  const [sub, setSub] = useState(null);
  const [seats, setSeats] = useState(1);
  const [busy, setBusy] = useState(null);

  const load = useCallback(async () => {
    try {
      const [cfg, subscription] = await Promise.all([BillingApi.config(), BillingApi.subscription()]);
      setConfig(cfg);
      setSub(subscription);
      setSeats(Math.max(1, subscription?.usage?.seats_used || 1));
    } catch (e) {
      toast.error(errorMessage(e, "Could not load billing information"));
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const startCheckout = async (planKey) => {
    setBusy(planKey);
    try {
      const res = await BillingApi.checkout({
        plan: planKey,
        seats: Number(seats) || 1,
        origin_url: window.location.origin,
      });
      toast.info(t("billing.redirecting"));
      window.location.href = res.checkout_url;
    } catch (e) {
      toast.error(errorMessage(e, "Could not start checkout"));
      setBusy(null);
    }
  };

  if (!config || !sub) {
    return (
      <AdminShell title={t("billing.title")}>
        <div className="flex h-64 items-center justify-center">
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        </div>
      </AdminShell>
    );
  }

  const usage = sub.usage || {};
  const scanPct = usage.included_scans
    ? Math.min(100, Math.round((usage.scans_used / usage.included_scans) * 100))
    : 0;
  const seatPct = seatPercent(usage.seats_used, usage.max_seats, usage.unlimited_seats);
  const currentPlan = sub.plan?.key;
  const status = sub.subscription?.status || "trialing";

  return (
    <AdminShell
      title={t("billing.title")}
      subtitle={t("billing.sub")}
      actions={
        <Button variant="outline" size="sm" onClick={load} data-testid="billing-refresh">
          <RefreshCw className="h-4 w-4" />
        </Button>
      }
    >
      <div className="grid gap-4 lg:grid-cols-3">
        <div className="ll-card p-4">
          <div className="flex items-center gap-2 text-xs uppercase tracking-wide text-muted-foreground">
            <CreditCard className="h-3.5 w-3.5" /> {t("billing.currentPlan")}
          </div>
          <div className="mt-2 font-display text-2xl font-semibold" data-testid="current-plan-name">
            {planName(t, sub.plan?.key, sub.plan?.name)}
          </div>
          <p className="mt-1 text-sm capitalize text-muted-foreground">
            {t("billing.statusLabel")}:{" "}
            {t(`billing.statuses.${status}`).startsWith("billing.statuses.")
              ? status
              : t(`billing.statuses.${status}`)}
          </p>
          {sub.subscription?.trial_ends_at && status === "trialing" ? (
            <p className="mt-1 text-xs text-muted-foreground">
              {t("billing.trialEnds")} {formatDateTime(sub.subscription.trial_ends_at)}
            </p>
          ) : null}
        </div>

        <div className="ll-card p-4">
          <div className="flex items-center gap-2 text-xs uppercase tracking-wide text-muted-foreground">
            <Gauge className="h-3.5 w-3.5" /> {t("billing.scansThisMonth")}
          </div>
          <div className="ll-num mt-2 font-display text-2xl font-semibold" data-testid="usage-scans">
            {usage.scans_used} / {usage.included_scans}
          </div>
          <Progress value={scanPct} className="mt-2 h-1.5" />
          <p className="mt-2 text-xs text-muted-foreground">
            {t("billing.billingPeriod")} {usage.period}
          </p>
        </div>

        <div className="ll-card p-4">
          <div className="flex items-center gap-2 text-xs uppercase tracking-wide text-muted-foreground">
            <Users className="h-3.5 w-3.5" /> {t("billing.activeSeats")}
          </div>
          <div className="ll-num mt-2 font-display text-2xl font-semibold" data-testid="usage-seats">
            {usage.seats_used} / {seatLimitLabel(t, usage.max_seats, usage.unlimited_seats)}
          </div>
          {seatPct !== null ? <Progress value={seatPct} className="mt-2 h-1.5" /> : null}
          <p className="mt-2 text-xs text-muted-foreground">{t("billing.seatsHint")}</p>
        </div>
      </div>

      <div className="ll-card mt-5 p-4">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <h2 className="font-display text-base font-semibold">{t("billing.thisMonthUsage")}</h2>
            <p className="text-sm text-muted-foreground">
              {t("billing.platformFee")} + {sub.current_quote?.seats} {t("billing.seatsWord")}
              {sub.current_quote?.extra_scans
                ? ` + ${sub.current_quote.extra_scans} ${t("billing.extraScans")}`
                : ` · ${t("billing.noOverage")}`}
            </p>
          </div>
          <div className="ll-num font-display text-3xl font-semibold" data-testid="current-quote-total">
            {formatMoney((sub.current_quote?.total_cents || 0) / 100, "EUR")}
          </div>
        </div>
      </div>

      <div className="mt-6">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h2 className="font-display text-lg font-semibold">{t("billing.choosePlan")}</h2>
            <p className="text-sm text-muted-foreground">
              {t("billing.choosePlanSub")}
            </p>
          </div>
          <div className="flex items-end gap-2">
            <div className="space-y-1">
              <Label htmlFor="seatcount" className="text-xs">
                {t("billing.seatsToBuy")}
              </Label>
              <Input
                id="seatcount"
                type="number"
                min={1}
                className="ll-num h-9 w-24"
                value={seats}
                onChange={(e) => setSeats(e.target.value)}
                data-testid="seats-input"
              />
            </div>
          </div>
        </div>

        <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
          {(config.plans || []).map((plan) => {
            const monthly = (plan.base_cents + plan.seat_cents * (Number(seats) || 1)) / 100;
            const isCurrent = plan.key === currentPlan;
            return (
              <div
                key={plan.key}
                className={`ll-card flex flex-col p-4 ${
                  plan.popular ? "border-primary ring-1 ring-primary/30" : ""
                }`}
                data-testid={`plan-card-${plan.key}`}
              >
                <div className="flex items-center justify-between">
                  <h3 className="font-display text-base font-semibold" data-testid={`plan-name-${plan.key}`}>
                    {planName(t, plan.key, plan.name)}
                  </h3>
                  {plan.popular ? (
                    <span className="rounded-full bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary">
                      {t("billing.popular")}
                    </span>
                  ) : null}
                  {isCurrent ? (
                    <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-700">
                      {t("billing.current")}
                    </span>
                  ) : null}
                </div>
                <p className="mt-1 text-xs text-muted-foreground">{planTagline(t, plan.key, plan.tagline)}</p>
                <div className="mt-3">
                  <div className="ll-num font-display text-2xl font-semibold">
                    {plan.purchasable ? formatMoney(monthly, "EUR") : t("common.free")}
                  </div>
                  <p className="ll-num text-xs text-muted-foreground">
                    {plan.purchasable
                      ? `${formatMoney(plan.base_eur, "EUR")} + ${formatMoney(
                          plan.seat_eur,
                          "EUR"
                        )} × ${Number(seats) || 1} ${t("billing.seatsWord")} ${t("common.perMonth")}`
                      : t("landing.trialNote")}
                  </p>
                </div>
                <ul className="mt-3 flex-1 space-y-1.5 text-sm text-muted-foreground">
                  {planFeatures(t, plan.key, plan.features || []).map((f) => (
                    <li key={f} className="flex items-start gap-2">
                      <CheckCircle2 className="mt-0.5 h-3.5 w-3.5 shrink-0 text-primary" /> {f}
                    </li>
                  ))}
                  {plan.purchasable ? (
                    <li className="ll-num flex items-start gap-2">
                      <CheckCircle2 className="mt-0.5 h-3.5 w-3.5 shrink-0 text-primary" />
                      {t("billing.perExtraScan", { price: formatMoney(plan.extra_scan_eur, "EUR") })}
                    </li>
                  ) : null}
                </ul>
                {plan.purchasable ? (
                  <Button
                    className="mt-4"
                    variant={plan.popular ? "default" : "secondary"}
                    disabled={!config.features?.checkout || busy === plan.key}
                    onClick={() => startCheckout(plan.key)}
                    data-testid={`billing-checkout-button-${plan.key}`}
                  >
                    {busy === plan.key ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                    {isCurrent
                      ? t("billing.updateSubscription")
                      : t("billing.subscribeTo", { plan: planName(t, plan.key, plan.name) })}
                  </Button>
                ) : (
                  <Button className="mt-4" variant="outline" disabled>
                    {t("billing.includedOnSignup")}
                  </Button>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {sub.transactions?.length ? (
        <div className="ll-card mt-6 overflow-hidden">
          <div className="border-b px-4 py-3">
            <h2 className="font-display text-sm font-semibold">{t("billing.checkoutHistory")}</h2>
          </div>
          <Table data-testid="billing-transactions-table">
            <TableHeader className="bg-muted/40">
              <TableRow>
                <TableHead>{t("billing.colStarted")}</TableHead>
                <TableHead>{t("common.plan")}</TableHead>
                <TableHead className="text-right">{t("billing.colSeats")}</TableHead>
                <TableHead className="text-right">{t("billing.colAmount")}</TableHead>
                <TableHead>{t("billing.colPayment")}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {sub.transactions.map((t) => (
                <TableRow key={t.session_id}>
                  <TableCell className="text-sm">{formatDateTime(t.created_at)}</TableCell>
                  <TableCell className="text-sm capitalize">{t.plan}</TableCell>
                  <TableCell className="ll-num text-right">{t.seats}</TableCell>
                  <TableCell className="ll-num text-right">{formatMoney(t.amount, "EUR")}</TableCell>
                  <TableCell className="text-sm capitalize">{t.payment_status}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      ) : null}
    </AdminShell>
  );
};

export default Billing;
